import { css, html, LitElement, PropertyValues, render, unsafeCSS } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import mapboxgl, { GeoJSONSource, Map as MapboxMap, MapMouseEvent, Popup } from 'mapbox-gl'
import mapboxCss from 'mapbox-gl/dist/mapbox-gl.css?inline'
import { countries } from './Countries'
import { Bounds, Country, Dealer, MapSettings } from './Types'
import { DealerPopup } from './Popup'

@customElement('lindberg-dealers-map')
export class DealerMap extends LitElement {
	static styles = css`
		:host {
			display: block;
			container-type: size;
		}

		#container {
			height: 100cqh;
		}

		#map { 
			width: 100%; 
			height: 100%; 
		}
		${unsafeCSS(mapboxCss)}
		.mapboxgl-map { font-family: inherit; }
		.mapboxgl-popup-tip{
			/* display:none; */
			/* border-radius: 100%; */

		}
		.mapboxgl-popup-content {
			background:none;
			border-radius: none;
			box-shadow: none;
			padding: none;			
			outline-color: var(--color-primary-gray-400);
			border-radius: 10px;
			outline-offset: -1px;
			background-color: white;
		}
	`

	@property({ type: Array })
	dealers: Dealer[] = []

	@property({ type: Object })
	settings?: MapSettings

	private map!: MapboxMap
	private popup?: Popup

	@query('#container')
	private mapContainer?: HTMLDivElement


	@property({ attribute: false })
	private _selectedDealer: Dealer | null = null

	public get selectedDealer(): Dealer | null {
		return this._selectedDealer
	}
	public set selectedDealer(value: Dealer | null) {
		if (value == this._selectedDealer) return
		this._selectedDealer = value
		if (!this.map) return
		if (value) {
			const center = value.geometry.coordinates
			if (this.popup) this.popup.setLngLat(center).addTo(this.map)
			let zoom = Math.max(this.map.getZoom(), 10)
			this.map.flyTo({ center, zoom, essential: true })
		} else if (this.popup) {
			this.popup.remove()
		}
	}

	@property({ type: Object })
	private _selectedCountry: Country | null = null
	public get selectedCountry(): Country | null {
		return this._selectedCountry
	}

	public set selectedCountry(value: Country | null) {
		if (value === this._selectedCountry) return
		this._selectedCountry = value
		this.applyCountryFill()
	}

	private hoveredId: number | string | null = null
	private mapReady = false
	private originalFill: any

	private applyCountryFill() {
		if (!this.mapReady) return
		this.map.setPaintProperty('country-boundaries', 'fill-color', [
			'case',
			['==', ['get', 'iso_3166_1'], this._selectedCountry?.code ?? ''],
			'rgb(229,214,188)', // selected

			['boolean', ['feature-state', 'hover'], false],
			'rgb(234, 222, 203)', // hover

			this.originalFill
		])
	}

	connectedCallback() {
		super.connectedCallback()
		mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN
	}

	setBounds(bounds: Bounds, progammatic = true) {
		this.map.fitBounds([[bounds.west, bounds.south], [bounds.east, bounds.north]], {}, { programmatic: progammatic })
		console.log(bounds)
	}

	setCenter(center: { latitude: number; longitude: number }, zoom?: number, programmatic = true) {
		this.map.flyTo({ center: [center.longitude, center.latitude], zoom }, { programmatic })
	}

	firstUpdated() {
		this.map = new mapboxgl.Map({
			container: this.mapContainer as HTMLElement,
			style: 'mapbox://styles/boye/cmhyrqfir002g01slb3ir8pk9',
			center: [10, 50],
			zoom: 5,
			pitch: 0,
			bearing: 0,
			pitchWithRotate: false,
			maxPitch: 0,
			attributionControl: false
		})
		this.map.touchZoomRotate.disableRotation()
		this.map.keyboard.disableRotation()

		this.map.setMaxZoom(18)

		this.map.addControl(new mapboxgl.NavigationControl({ 'showCompass': false }), 'top-right')
		this.map.on('load', () => this.mapLoaded())

		this.map.on('click', 'circle-layer', this.onMarkerClick.bind(this))
		// this.map.on('click', 'country-label', this.onLabelClick.bind(this))

		this.map.on('click', e => {
			if (!this._selectedDealer) return
			const layers = ['circle-layer', 'clusters'].filter(id => this.map.getLayer(id))
			if (this.map.queryRenderedFeatures(e.point, { layers }).length > 0) return
			this.dispatchEvent(new CustomEvent('dealer-selected', { bubbles: true, detail: { dealer: null } }))
		})

		this.map.on('moveend', e => {
			if ((e as any).programmatic) return
			const bounds = this.map.getBounds()
			if (!bounds) return
			this.dispatchEvent(new CustomEvent('map-moved', { bubbles: true, detail: { bounds: { north: bounds.getNorth(), west: bounds.getWest(), south: bounds.getSouth(), east: bounds.getEast()}, zoom: this.map.getZoom() } }))
		})
	}
	mapLoaded() {
		this.map.addSource('dealers', {
			type: 'geojson',
			data: {
				type: 'FeatureCollection',
				features: this.dealers
			},
			cluster: true,
			clusterMaxZoom: 12,
			clusterRadius: 25,
			clusterProperties: {
				maxDealerScore: ['max', ['get', 'score']]
			}
		})

		this.map.addLayer({
			id: 'clusters',
			type: 'circle',
			source: 'dealers',
			filter: ['has', 'point_count'],
			paint: {
				'circle-color': '#17505e',
				'circle-radius': [
					'interpolate',
					['linear'],
					['zoom'],
					2,
					10,
					20,
					['+', 10, ['*', 20, ['/', ['-', ['get', 'maxDealerScore'], 100], 100]]]
				]
			}
		})

		this.map.addLayer({
			id: 'circle-layer',
			type: 'circle',
			source: 'dealers',
			filter: ['!', ['has', 'point_count']],
			paint: {
				'circle-radius': [
					'interpolate',
					['linear'],
					['zoom'],
					2,
					['+', 10, ['*', 5, ['/', ['-', ['get', 'score'], 100], 100]]],
					20,
					['+', 15, ['*', 20, ['/', ['-', ['get', 'score'], 100], 100]]]
				],
				'circle-color': '#17505e'
			}
		})

		const popupElement = this.querySelector('lindberg-dealers-popup') as DealerPopup
		this.popup = new mapboxgl.Popup({ closeOnClick: false, closeButton: false, maxWidth: 'none' }).setDOMContent(popupElement)//.addTo(this.map);

		this.map.on('click', 'clusters', e => {
			const feature = e.features?.[0]
			if (!feature) return

			const clusterId = feature.properties?.cluster_id
			if (clusterId == null) return

			// TS can be annoying here, so cast as any if needed
			const src = this.map.getSource('dealers') as any

			// Get all leaves (points) in this cluster
			// Use a high limit; unless you have thousands of dealers in one cluster, this is fine.
			src.getClusterLeaves(clusterId, 1000, 0, (err: any, leaves: any[]) => {
				if (err || !leaves?.length) return

				const bounds = new mapboxgl.LngLatBounds()

				leaves.forEach(f => bounds.extend(f.geometry.coordinates))

				this.map.fitBounds(bounds, {
					padding: 80,
					// let it zoom quite far in so tight clusters can fully “explode”
					maxZoom: 16
				})
			})
		})

		// optional: pointer cursor on cluster hover
		this.map.on('mouseenter', 'clusters', () => {
			this.map.getCanvas().style.cursor = 'pointer'
		})
		this.map.on('mouseleave', 'clusters', () => {
			this.map.getCanvas().style.cursor = ''
		})

		const source = 'composite'
		const sourceLayer = 'country_boundaries' // adjust if yours is different

		this.originalFill = this.map.getPaintProperty('country-boundaries', 'fill-color')
		this.mapReady = true
		this.applyCountryFill()

		// Disputed territories (e.g. Taiwan) are represented in Mapbox's country_boundaries
		// tileset as overlapping overlay polygons tagged with a `worldview` property. Without
		// a filter, the China-claims-Taiwan overlay sits on top of Taiwan, so hovering/clicking
		// Taiwan resolves to CN. Restrict rendering to a single worldview to remove the overlap.
		const worldview = (this.settings?.worldView ?? 'us').toUpperCase()
		const worldviewFilter = [
			'any',
			['==', 'all', ['get', 'worldview']],
			['in', worldview, ['get', 'worldview']],
		] as any
		const existingFilter = this.map.getFilter('country-boundaries')
		this.map.setFilter(
			'country-boundaries',
			(existingFilter ? ['all', existingFilter, worldviewFilter] : worldviewFilter) as any,
		)

		this.map.on('mousemove', 'country-boundaries', e => {
			const feature = e.features?.[0]
			// console.log("HOVER", feature)
			if (!feature || feature.id == null) return

			const id = feature.id
			if (id === this.hoveredId) return

			if (this.hoveredId != null) {
				this.map.setFeatureState(
					{ source, sourceLayer, id: this.hoveredId },
					{ hover: false }
				)
			}
			this.hoveredId = null

			const country = countries.find(x => x.code == feature.properties?.iso_3166_1)
			if (!country) return

			this.map.setFeatureState({ source, sourceLayer, id }, { hover: true })

			this.hoveredId = id
		})

		this.map.on('mouseleave', 'country-boundaries', () => {
			if (this.hoveredId != null) {
				this.map.setFeatureState({ source, sourceLayer, id: this.hoveredId }, { hover: false })
				this.hoveredId = null
			}
		})

		this.map.on('click', 'country-boundaries', e => {
			const feature = e.features?.[0]
			if (!feature || feature.id == null || feature.properties?.iso_3166_1 === this.selectedCountry?.code) return

			const country = countries.find(x => x.code == feature.properties?.iso_3166_1)
			if (country) {
				this.selectedCountry = country
				this.dispatchEvent(new CustomEvent('country-selected', { bubbles: true, detail: { country } }))
			}
		})
	}

	onMarkerClick(e: MapMouseEvent) {
		const dealer = this.dealers.find(d => d.properties.entryNo == e.features?.at(-1)?.properties?.entryNo)
		if (dealer && dealer != this.selectedDealer) {
			const event = new CustomEvent('dealer-selected', { bubbles: true, detail: { dealer } })
			this.dispatchEvent(event)
		}
	}

	protected updated(changed: PropertyValues): void {
		if (changed.has('dealers')) {
			const source = this.map.getSource('dealers') as GeoJSONSource
			if (source) {
				source.setData({
					'type': 'FeatureCollection',
					'features': this.dealers
				})
			}
		}
	}

	render = () => html`<slot></slot><div id="container"></div>`
}
